/* eslint-disable @typescript-eslint/no-explicit-any */
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { Toast } from 'sharedUi/Toast';
import PageSkeleton from '../../components/common/page-skeleton';
import ScreenHeader from '../../components/common/screen-header';
import { Message } from 'primereact/message';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'sharedUi/Button';
import { Input } from 'sharedUi/Input';
import { TextArea } from 'sharedUi/TextArea';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import FormSelect from '../../components/forms/form-select';
import { useAccessibleProjects, projectFieldOptions, appIdFromField, defaultProjectId, referencableIn, SHARED_PROJECT } from '../../hooks/useProjectScope';
import FunctionChainEditor from '../../components/forms/function-chain-editor';
import ActionAttachmentEditor from '../../components/forms/action-attachment-editor';
import { recipientMastersService } from '../../services/recipient-masters.service';
import { valueSetsService } from '../../services/value-sets.service';
import { recipientListsService } from '../../services/recipient-lists.service';
import type { FunctionAttachment, ActionAttachment } from '../../services/recipient-masters.service';
import { extractErrorMessage } from '../../utils/error-handler';

const schema = z.object({
  // Empty means shared; the API honours this only inside the caller's scope.
  // Required: a blank appId is what the API reads as "shared with every
  // project", so an unset field would quietly author a global row.
  appId: z.string().min(1, 'Choose the project this belongs to.'),
  recipientCode: z.string().min(1, 'Recipient code is required'),
  recipientType: z.string().min(1, 'Type is required'),
  description: z.string().max(1000).optional().or(z.literal('')),
  recipientListId: z.string().optional().nullable(),
  functions: z.array(z.any()).optional(),
  actions: z.array(z.any()).optional(),
}).refine(
  // The API refuses these too. Caught here so each reads as a missing field on
  // the form rather than a 400 after the person has filled the rest in.
  (d) => d.recipientType !== 'FUNCTION' || (d.functions || []).length > 0,
  { message: 'A FUNCTION recipient needs at least one function', path: ['functions'] },
).refine(
  // A LIST recipient names no one itself, so without a list it resolves to
  // nobody, every time, and nothing at send reports it.
  (d) => d.recipientType !== 'LIST' || !!d.recipientListId,
  { message: 'Choose the list this reaches', path: ['recipientListId'] },
);
type FormData = z.infer<typeof schema>;

const _vsOpts = (vs: any) => (vs?.items || []).map((i: any) => ({ value: i.code, label: i.label }));

/** What each recipient type actually does, said where the choice is made. */
const TYPE_HINT: Record<string, string> = {
  LIST: 'Reaches whoever is on a recipient list — people are named there, not here.',
  FUNCTION: 'Runs a chain of functions to work out who — built below, in order.',
  AI_AGENT: 'Resolved by an AI agent. What that does is not defined yet.',
};

const RecipientMasterForm = () => {
  const { id, isReady, navigateToList } = useSecureNavigation({ entity: 'recipient', basePath: '/recipient-masters' });
  const isEdit = Boolean(id);
  const { projects, canScopeByProject } = useAccessibleProjects();
  const queryClient = useQueryClient();
  const toast = useRef<Toast>(null);

  const { control, handleSubmit, reset, watch, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      appId: defaultProjectId(projects),
      recipientCode: '', recipientType: 'LIST', description: '',
      recipientListId: null, functions: [], actions: [],
    },
    mode: 'onChange',
  });
  const recipientType = watch('recipientType');
  // What this recipient may reference: its own project's rows and shared ones.
  // Shared itself (undefined) references anything, which is what the API allows.
  const projectId = appIdFromField(watch('appId'));

  const { data: listsPage } = useQuery({
    queryKey: ['recipientLists', 'recipientPicker'],
    queryFn: () => recipientListsService.list({ skip: 0, limit: 200 }),
    staleTime: 3e5,
  });
  // A do-not-contact list is a suppression list, not an audience — offering it
  // here would let somebody address the people who asked not to be addressed.
  // Nor another project's list: the save resolves it against THIS recipient's
  // project and refuses anything outside it.
  const listOptions = ((listsPage as any)?.data || [])
    .filter((l: any) => l.isActive !== false && !l.isDoNotContact)
    .filter(referencableIn(projectId))
    .map((l: any) => ({ value: l._id, label: l.recipientListCode }));

  const { data: typeVS } = useQuery({ queryKey: ['vs', 'recipientType'], queryFn: () => valueSetsService.getByKey('recipientType', 'en'), staleTime: 3e5 });

  const { data: recipientData, isLoading } = useQuery({
    queryKey: ['recipientMaster', id],
    queryFn: () => recipientMastersService.getById(id!),
    enabled: isEdit && isReady && !!id,
  });

  useEffect(() => {
    if (recipientData && isEdit) {
      reset({
        appId: recipientData.appId || SHARED_PROJECT,
        recipientCode: recipientData.recipientCode || '',
        recipientType: recipientData.recipientType || 'LIST',
        description: recipientData.description || '',
        recipientListId: (recipientData as any).recipientListId
          ? String((recipientData as any).recipientListId) : null,
        functions: recipientData.functions || [],
        actions: recipientData.actions || [],
      });
    }
  }, [recipientData, isEdit, reset]);

  const mutation = useMutation({
    mutationFn: (data: any) => (isEdit && id ? recipientMastersService.update(id, data) : recipientMastersService.create(data)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipientMasters'] });
      toast.current?.show({ severity: 'success', summary: 'Success', detail: `Recipient ${isEdit ? 'updated' : 'created'}`, life: 3000 });
      setTimeout(() => navigateToList(), 800);
    },
    onError: (e: any) => toast.current?.show({ severity: 'error', summary: 'Error', detail: extractErrorMessage(e, 'Save failed'), life: 10000 }),
  });

  const onSubmit = (d: FormData) => mutation.mutate({
    // Create only: the update schema has no appId, so sending one on edit
    // would look like a move that silently did not happen.
    ...(isEdit ? {} : { appId: appIdFromField(d.appId) }),
    recipientCode: d.recipientCode,
    recipientType: d.recipientType,
    description: d.description || null,
    // Each kind carries only its own shape. Sending the others would store
    // configuration nothing reads, which reads as configuration that does
    // something — the chain of a recipient that was switched to LIST, say.
    recipientListId: d.recipientType === 'LIST' ? (d.recipientListId || null) : null,
    functions: d.recipientType === 'FUNCTION' ? (d.functions as FunctionAttachment[]) : [],
    // Actions belong to the recipient, not to how it resolves: any of the three
    // may offer somebody a button.
    actions: (d.actions || []) as ActionAttachment[],
  });

  if (isEdit && isLoading) return <PageSkeleton />;

  return (
    <div className="p-4" data-testid="SCR-RecipientMaster-Form">
      <Toast ref={toast} position="top-right" />
      <ScreenHeader icon="pi pi-user" title={isEdit ? 'Edit Recipient' : 'Create Recipient'} onBack={() => navigateToList()} />
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4 max-w-5xl w-full">
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {canScopeByProject && (
            <Controller name="appId" control={control} render={({ field }) => (
              <FormSelect label="Project" name={field.name} value={field.value || ''}
                options={projectFieldOptions(projects, field.value)}
                onChange={field.onChange} disabled={isEdit}
                helperText={isEdit
                  ? 'Set when this was created; the API does not move one afterwards.'
                  : 'Which project owns this. It cannot be moved afterwards.'} />
            )} />
          )}
          <Controller name="recipientCode" control={control} render={({ field }) => (
            <Input label="Recipient code" required {...field} error={errors.recipientCode?.message} placeholder="UNIT_SUPERVISORS" disabled={isEdit} />
          )} />
          <Controller name="recipientType" control={control} render={({ field }) => (
            <FormSelect label="Type" required name={field.name} value={field.value || ''} options={_vsOpts(typeVS)} onChange={field.onChange} error={!!errors.recipientType} helperText={errors.recipientType?.message || TYPE_HINT[field.value || '']} />
          )} />
          <Controller name="description" control={control} render={({ field }) => (
            <div className="sm:col-span-2"><TextArea label="Description" {...field} rows={2} /></div>
          )} />
        </div>

        {recipientType === 'LIST' && (
          <div className="ds-card p-5 flex flex-col gap-3">
            <Controller name="recipientListId" control={control} render={({ field }) => (
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Recipient list</label>
                <Dropdown
                  value={field.value || null}
                  options={listOptions}
                  optionLabel="label"
                  optionValue="value"
                  filter
                  showClear
                  placeholder="Select a list"
                  emptyMessage="No lists yet — create one under Recipient Lists."
                  className="w-full"
                  data-testid="RecipientMasterForm.List"
                  onChange={(e) => field.onChange(e.value || null)}
                />
                <small className="text-xs text-gray-400">
                  People are named in the list, not here — so the same list can serve several
                  recipients.
                </small>
                {errors.recipientListId && (
                  <small className="text-red-500">{errors.recipientListId.message as string}</small>
                )}
              </div>
            )} />

          </div>
        )}

        {recipientType === 'AI_AGENT' && (
          <Message
            severity="warn"
            className="w-full"
            text="Not built yet. You can save one, but it will reach nobody and the fire will say so."
          />
        )}

        {recipientType === 'FUNCTION' && (
          <div className="ds-card p-5">
            <Controller name="functions" control={control} render={({ field }) => (
              <FunctionChainEditor
                projectId={projectId}
                value={(field.value || []) as FunctionAttachment[]}
                onChange={field.onChange}
                testId="RecipientMasterForm.Chain"
              />
            )} />
            {errors.functions && <small className="text-red-500">{errors.functions.message as string}</small>}
            <small className="block text-xs text-gray-400 mt-2">
              Steps run top to bottom, and each may only read what an earlier one produced.
              The chain is checked when you save.
            </small>
          </div>
        )}

        {/* Actions apply to every type: whoever this resolves to may be offered
            something to press, however they were found. */}
        <div className="ds-card p-5">
          <Controller name="actions" control={control} render={({ field }) => (
            <ActionAttachmentEditor
              projectId={projectId}
              value={(field.value || []) as ActionAttachment[]}
              onChange={field.onChange}
              testId="RecipientMasterForm.Actions"
            />
          )} />
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" label="Cancel" severity="secondary" outlined onClick={() => navigateToList()} />
          <Button type="submit" label={mutation.isPending ? 'Saving…' : isEdit ? 'Update' : 'Create'} icon={mutation.isPending ? 'pi pi-spin pi-spinner' : 'pi pi-check'} disabled={mutation.isPending} />
        </div>
      </form>
    </div>
  );
};

export default RecipientMasterForm;
